import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../common/LoadingSpinner';
import SessionExpiredModal from '../common/SessionExpiredModal';
import ErrorModal from '../common/ErrorModal';
import BookingTimer from './hotel-details/BookingTimer';
import { useBookingSession } from '../../hooks/useBookingSession';
import agentApiService from '../../services/agentApiService';

const AgentCustomerDetailsPage = () => {
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL parameters
  const roomBookingCode = searchParams.get('roomBookingCode');
  const searchSessionId = searchParams.get('searchSessionId');
  const destination = searchParams.get('destination');
  const checkInDate = searchParams.get('checkInDate');
  const checkOutDate = searchParams.get('checkOutDate');
  const roomAllocations = searchParams.get('roomAllocations');
  const provider = searchParams.get('provider');
  const timerKey = searchParams.get('timerKey');
  const reserveCode1 = searchParams.get('reserveCode');
  const hotelDetailsKey = searchParams.get('hotelDetailsKey'); // Redis key for hotelDetails

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [timeoutProgress, setTimeoutProgress] = useState(0);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [passengerDetails, setPassengerDetails] = useState({
    rooms: [],
    gstInfo: {
      gstNumber: '',
      gstCompanyName: '',
      gstCompanyAddress: '',
      gstCompanyContactNumber: '',
      gstCompanyEmail: ''
    }
  });
  const [commonInfo, setCommonInfo] = useState({
    mobile: '',
    email: '',
    country: '',
    pan: '',
    passportNo: '',
    passportIssueDate: '',
    passportExpDate: '',
    gstNumber: '',
    gstCompanyName: '',
    gstCompanyAddress: '',
    gstCompanyContactNumber: '',
    gstCompanyEmail: ''
  });
  const [specialRequests, setSpecialRequests] = useState({
    requestText: '',
    closeRooms: false,
    freeParking: false
  });
  const [arrivalInfo, setArrivalInfo] = useState({
    arrivalDate: '',
    arrivalTime: ''
  });
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPriceChangeModal, setShowPriceChangeModal] = useState(false);
  const [priceChangeInfo, setPriceChangeInfo] = useState(null);
  
  // Get prebookKey from sessionStorage on mount (persists across page refresh)
  const [prebookKey, setPrebookKey] = useState(() => {
    const storageKey = `prebookKey_${roomBookingCode}`;
    return sessionStorage.getItem(storageKey) || null;
  });
  
  // Ref to prevent duplicate API calls
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Use timerKey as expiryTime (timerKey contains the expiry timestamp)
  const expiryTime = timerKey ? parseInt(timerKey, 10) : null;
  const { isExpired, hasValidSession, getTimeRemaining } = useBookingSession(
    expiryTime,
    () => setShowSessionExpiredModal(true) // Show modal when session expires
  );

  // Fetch customer detail data on component mount
  useEffect(() => {
    console.log('AgentCustomerDetailsPage: Component mounted');
    console.log('🔍 URL Parameters:', {
      roomBookingCode,
      searchSessionId,
      hotelId,
      provider,
      destination,
      checkInDate,
      checkOutDate,
      hotelDetailsKey
    });
    
    // Check if prebookKey exists in sessionStorage
    const storageKey = `prebookKey_${roomBookingCode}`;
    const storedKey = sessionStorage.getItem(storageKey);
    console.log('🔑 prebookKey from sessionStorage:', storedKey || 'null (first visit)');
    
    // Prevent duplicate calls using ref
    if (hasFetchedRef.current || isFetchingRef.current) {
      console.log('⚠️ API call already made or in progress, skipping duplicate call');
      return;
    }
    
    console.log('Calling fetchCustomerData...');
    hasFetchedRef.current = true;
    fetchCustomerData();
    
    // Cleanup: Remove prebookKey from sessionStorage when component unmounts
    return () => {
      // Don't remove on unmount, only when tab closes
      // sessionStorage automatically clears when tab closes
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const fetchCustomerData = async () => {
    // Prevent concurrent calls
    if (isFetchingRef.current) {
      console.log('⚠️ Fetch already in progress, skipping duplicate call');
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      console.log('🔍 Starting fetchCustomerData...');
      console.log('🔑 hotelDetailsKey from URL:', hotelDetailsKey);
      console.log('🔑 prebookKey from state:', prebookKey);

      // Build request body with all parameters including hotelDetailsKey and prebookKey
      const requestBody = {
        roomBookingCode: roomBookingCode || '',
        searchSessionId: searchSessionId || '',
        hotelId: hotelId || '',
        destination: destination || '',
        checkInDate: checkInDate || '',
        checkOutDate: checkOutDate || '',
        roomAllocations: roomAllocations || '',
        provider: provider || '',
        timerKey: timerKey || '',
        hotelDetailsKey: hotelDetailsKey || '', // Pass Redis key to backend
        prebookKey: prebookKey || null, // Pass prebookKey for page refresh (null on first visit)
      };

      if (reserveCode1) {
        requestBody.reserveCode = reserveCode1;
      }

      console.log('🚀 Calling API with request body:', {
        roomBookingCode,
        searchSessionId,
        hotelId,
        provider,
        hotelDetailsKey,
        prebookKey: prebookKey ? 'Present' : 'Null (First visit)',
      });

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      console.log('Calling agentApiService.getCustomerDetail with request body');
      const response = await agentApiService.getCustomerDetail(requestBody);

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        console.log('Customer data received:', response);
        console.log('prebookKey received:', response.prebookKey);
        console.log('roomCardDetails:', response.roomCardDetails);
        console.log('room object:', response.roomCardDetails?.room);
        console.log('validations:', response.roomCardDetails?.room?.validations);
        console.log('isPanMandatory:', response.roomCardDetails?.room?.validations?.panMandatory);
        console.log('isPassportMandatory:', response.roomCardDetails?.room?.validations?.passportMandatory);
        console.log('isGstAllowed:', response.roomCardDetails?.room?.validations?.gstAllowed);
        
        // Store prebookKey for page refresh (both in state and sessionStorage)
        if (response.prebookKey) {
          const storageKey = `prebookKey_${roomBookingCode}`;
          setPrebookKey(response.prebookKey);
          sessionStorage.setItem(storageKey, response.prebookKey);
          console.log('✅ Stored prebookKey for page refresh:', response.prebookKey);
          console.log('✅ Saved to sessionStorage with key:', storageKey);
        }
        
        // Check for price change
        const roomFareDetails = response.roomCardDetails?.room?.roomFareDetails;
        if (roomFareDetails && roomFareDetails.differenceBetweenFare < 0) {
          // Price has decreased - show discount modal
          setPriceChangeInfo({
            originalPrice: roomFareDetails.bookingAfterPrice - roomFareDetails.differenceBetweenFare,
            newPrice: roomFareDetails.bookingAfterPrice,
            discount: Math.abs(roomFareDetails.differenceBetweenFare)
          });
          setShowPriceChangeModal(true);
        }
        
        setCustomerData(response);
        initializePassengerDetails(response.hotelDetailsSearchCriteria);
      } else {
        // Handle error response with closeTab flag
        if (response.closeTab) {
          setErrorMessage(response.message || 'Something went wrong. Please select room again.');
          setShowErrorModal(true);
        } else {
          throw new Error(response.message || 'Failed to fetch customer data');
        }
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setError(error.message);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      
      // Check if error response has closeTab flag
      if (error.response?.closeTab) {
        setErrorMessage(error.response.message || 'Something went wrong. Please select room again.');
        setShowErrorModal(true);
      } else {
        toast.error(`Failed to load customer data: ${error.message}`);
      }
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };
  const initializePassengerDetails = (hotelDetailsSearchCriteria) => {
    if (!hotelDetailsSearchCriteria || !hotelDetailsSearchCriteria.rooms) return;

    const rooms = hotelDetailsSearchCriteria.rooms.map((room, roomIndex) => ({
      roomNumber: roomIndex + 1,
      adults: Array.from({ length: room.adults }, () => ({
        passengerTitle: 'Mr.',
        passengerFirstName: '',
        passengerLastName: '',
        passengerPANCardNumber: '',
        passengerPhoneNumber: '',
        passengerEmail: '',
        passengerCountry: '',
        passengerPassportNo: '',
        passengerPassportIssueDate: '',
        passengerPassportExpDate: ''
      })),
      children: room.childAges ? room.childAges.map((age, childIndex) => ({
        passengerTitle: 'Master',
        passengerFirstName: '',
        passengerLastName: '',
        passengerAge: age, // Keep the age from hotelDetailsSearchCriteria
        passengerPassportNo: '',
        passengerPassportIssueDate: '',
        passengerPassportExpDate: ''
      })) : []
    }));

    setPassengerDetails(prev => ({
      ...prev,
      rooms
    }));
  };

  const handleInputChange = (roomIndex, passengerType, passengerIndex, field, value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms[roomIndex][passengerType][passengerIndex][field] = value;
      return newDetails;
    });
  };

  const handleGstChange = (field, value) => {
    setPassengerDetails(prev => ({
      ...prev,
      gstInfo: {
        ...prev.gstInfo,
        [field]: value
      }
    }));
  };
  const handleCommonInfoChange = (field, value) => {
    setCommonInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyCommonInfo = (field, value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms.forEach(room => {
        room.adults.forEach(adult => {
          if (field === 'mobile') adult.passengerPhoneNumber = value;
          if (field === 'email') adult.passengerEmail = value;
          if (field === 'country') adult.passengerCountry = value;
        });
      });
      return newDetails;
    });
  };

  const applyPanInfo = (value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms.forEach(room => {
        room.adults.forEach(adult => {
          adult.passengerPANCardNumber = value;
        });
      });
      return newDetails;
    });
  };

  const applyPassportInfo = (field, value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      newDetails.rooms.forEach(room => {
        [...room.adults, ...room.children].forEach(passenger => {
          if (field === 'passportNo') passenger.passengerPassportNo = value;
          if (field === 'passportIssueDate') passenger.passengerPassportIssueDate = value;
          if (field === 'passportExpDate') passenger.passengerPassportExpDate = value;
        });
      });
      return newDetails;
    });
  };

  const applyGstInfo = (field, value) => {
    setPassengerDetails(prev => {
      const newDetails = { ...prev };
      if (field === 'gstNumber') newDetails.gstInfo.gstNumber = value;
      if (field === 'gstCompanyName') newDetails.gstInfo.gstCompanyName = value;
      if (field === 'gstCompanyAddress') newDetails.gstInfo.gstCompanyAddress = value;
      if (field === 'gstCompanyContactNumber') newDetails.gstInfo.gstCompanyContactNumber = value;
      if (field === 'gstCompanyEmail') newDetails.gstInfo.gstCompanyEmail = value;
      return newDetails;
    });
  };

  const handleSpecialRequestChange = (field, value) => {
    setSpecialRequests(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrivalInfoChange = (field, value) => {
    setArrivalInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const validateForm = () => {
    const errors = [];

    passengerDetails.rooms.forEach((room, roomIndex) => {
      // Validate adults
      room.adults.forEach((adult, adultIndex) => {
        const prefix = `Room ${roomIndex + 1} Adult ${adultIndex + 1}`;
        if (!adult.passengerFirstName.trim()) errors.push(`${prefix}: First name is required`);
        if (!adult.passengerLastName.trim()) errors.push(`${prefix}: Last name is required`);
        if (!adult.passengerPhoneNumber.trim()) errors.push(`${prefix}: Phone number is required`);
        if (!adult.passengerCountry.trim()) errors.push(`${prefix}: Country is required`);
      });

      // Validate children
      room.children.forEach((child, childIndex) => {
        const prefix = `Room ${roomIndex + 1} Child ${childIndex + 1}`;
        if (!child.passengerFirstName.trim()) errors.push(`${prefix}: First name is required`);
        if (!child.passengerLastName.trim()) errors.push(`${prefix}: Last name is required`);
      });
    });

    // Check for duplicate names
    const allNames = new Set();
    let hasDuplicates = false;

    passengerDetails.rooms.forEach(room => {
      [...room.adults, ...room.children].forEach(passenger => {
        if (passenger.passengerFirstName && passenger.passengerLastName) {
          const fullName = `${passenger.passengerFirstName} ${passenger.passengerLastName}`.toLowerCase().trim();
          if (allNames.has(fullName)) {
            hasDuplicates = true;
          } else {
            allNames.add(fullName);
          }
        }
      });
    });

    if (hasDuplicates) {
      errors.push('Duplicate passenger names are not allowed');
    }

    // Check for duplicate passport numbers if passport is mandatory
    if (customerData?.roomCardDetails?.room?.validations?.passportMandatory) {
      const allPassports = new Set();
      let hasDuplicatePassports = false;

      passengerDetails.rooms.forEach(room => {
        [...room.adults, ...room.children].forEach(passenger => {
          if (passenger.passengerPassportNo && passenger.passengerPassportNo.trim()) {
            const passportNo = passenger.passengerPassportNo.trim().toUpperCase();
            if (allPassports.has(passportNo)) {
              hasDuplicatePassports = true;
            } else {
              allPassports.add(passportNo);
            }
          }
        });
      });

      if (hasDuplicatePassports) {
        errors.push('Duplicate passport numbers are not allowed');
      }
    }

    return errors;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check session validity before submission - show modal if expired
    if (isExpired || !hasValidSession) {
      setShowSessionExpiredModal(true);
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors.join('\n'));
      return;
    }

    try {
      setSubmitting(true);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      const response = await agentApiService.submitCustomerDetails(
        passengerDetails,
        prebookKey,
        hotelDetailsKey,
        roomBookingCode,
        timerKey
      );

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        toast.success('Passenger details saved successfully!');
        // Navigate to passenger summary page with reserveCode and prebookKey from response
        navigate(`/agent/dashboard/prebook/passenger-details?reserveCode=${response.reserveCode}&roomBookingCode=${roomBookingCode}&timerKey=${timerKey}&prebookKey=${encodeURIComponent(response.prebookKey || prebookKey || '')}`);
      } else {
        throw new Error(response.message || 'Failed to save passenger details');
      }
    } catch (error) {
      console.error('Error submitting passenger details:', error);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to save passenger details: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Time options for arrival time dropdown
  const timeOptions = [
    { value: '', label: 'Please select' },
    { value: 'dont-know', label: "I don't know" },
    { value: '00-01', label: '00:00 – 01:00' },
    { value: '01-02', label: '01:00 – 02:00' },
    { value: '02-03', label: '02:00 – 03:00' },
    { value: '03-04', label: '03:00 – 04:00' },
    { value: '04-05', label: '04:00 – 05:00' },
    { value: '05-06', label: '05:00 – 06:00' },
    { value: '06-07', label: '06:00 – 07:00' },
    { value: '07-08', label: '07:00 – 08:00' },
    { value: '08-09', label: '08:00 – 09:00' },
    { value: '09-10', label: '09:00 – 10:00' },
    { value: '10-11', label: '10:00 – 11:00' },
    { value: '11-12', label: '11:00 – 12:00' },
    { value: '12-13', label: '12:00 – 13:00' },
    { value: '13-14', label: '13:00 – 14:00' },
    { value: '14-15', label: '14:00 – 15:00' },
    { value: '15-16', label: '15:00 – 16:00' },
    { value: '16-17', label: '16:00 – 17:00' },
    { value: '17-18', label: '17:00 – 18:00' },
    { value: '18-19', label: '18:00 – 19:00' },
    { value: '19-20', label: '19:00 – 20:00' },
    { value: '20-21', label: '20:00 – 21:00' },
    { value: '21-22', label: '21:00 – 22:00' },
    { value: '22-23', label: '22:00 – 23:00' },
    { value: '23-24', label: '23:00 – 24:00' }
  ];
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <LoadingSpinner message="Loading customer details..." />
          
          {/* Timeout Progress Indicator */}
          {timeoutProgress > 0 && (
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ${
                    showTimeoutWarning ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${timeoutProgress}%` }}
                ></div>
              </div>
              <p className={`text-sm ${showTimeoutWarning ? 'text-yellow-600' : 'text-gray-600'}`}>
                {showTimeoutWarning 
                  ? 'Taking longer than expected... Please wait.'
                  : 'Connecting to prebook service...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Timeout in {Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))} seconds
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Customer Details</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchCustomerData}
            className="bg-red-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Customer Data Available</h3>
          <p className="text-gray-600">Unable to load customer details.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <BookingTimer timerKey={timerKey} expiryTime={expiryTime} />

      {/* Session Expired Warning */}
      {isExpired && (
        <div className="bg-red-50 border border-red-200 mx-4 mt-4 rounded-xl p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-red-800">Booking Session Expired</h3>
              <p className="text-red-700 mt-1">
                Your booking session has expired. Please refresh the page to start a new session.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.close()}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Hotel Book</h1>
                <p className="text-sm text-gray-600">Enter passenger information</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 mt-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
              <span className="ml-2 text-sm font-medium text-blue-600">Your Selection</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
              <span className="ml-2 text-sm font-medium text-blue-600">Your Details</span>
            </div>
            <div className="flex-1 h-px bg-gray-300"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-medium">3</div>
              <span className="ml-2 text-sm font-medium text-gray-500">Confirmation</span>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT SIDEBAR - Hotel & Room Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24 space-y-4">
              {/* Hotel Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {customerData.hotelDetails?.hotelName || 'Hotel Name'}
                </h3>
                
                {/* Hotel Star Rating */}
                {customerData.hotelDetails?.starRating && (
                  <div className="flex items-center mb-2">
                    <svg className="w-4 h-4 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      {customerData.hotelDetails.starRating} Star Hotel
                    </span>
                  </div>
                )}
                
                {/* Check-in Time */}
                <div className="flex items-center mb-2">
                  <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    Check-in: <span className="font-medium text-gray-900">{customerData.hotelDetails?.checkInTime || '14:00'}</span>
                  </span>
                </div>
                
                {/* Check-out Time */}
                <div className="flex items-center mb-2">
                  <svg className="w-4 h-4 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    Check-out: <span className="font-medium text-gray-900">{customerData.hotelDetails?.checkOutTime || '12:00'}</span>
                  </span>
                </div>
                
                {/* Address */}
                <div className="flex items-start mt-3">
                  <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-600 flex-1">
                    {(() => {
                      const address = customerData.hotelDetails?.address;
                      if (!address) return 'Address not available';
                      
                      // If address is an object, extract the completeAddress or build from parts
                      if (typeof address === 'object') {
                        return address.completeAddress || 
                               `${address.cityName || ''}, ${address.stateName || ''}, ${address.countryName || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',') ||
                               'Address not available';
                      }
                      
                      // If address is a string, return it directly
                      return address;
                    })()}
                  </span>
                </div>
              </div>
              
              {/* Room Information */}
              <div className="border-b pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Room Details</h4>
                
                {/* Room Category */}
                <div className="flex items-center mb-2">
                  <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {customerData.roomCardDetails?.room?.roomCategory?.[0] || 'Room Category'}
                  </span>
                </div>
                
                {/* Meal Type */}
                <div className="flex items-center mb-2">
                  <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {customerData.roomCardDetails?.room?.mealType || 'Meal plan not specified'}
                  </span>
                </div>
                
                {/* Guest Count */}
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-900">
                    {(() => {
                      const searchCriteria = customerData.hotelDetailsSearchCriteria;
                      if (!searchCriteria || !searchCriteria.rooms) return 'Guest info not available';
                      
                      const totalRooms = searchCriteria.rooms.length;
                      const totalAdults = searchCriteria.rooms.reduce((sum, room) => sum + (room.adults || 0), 0);
                      const totalChildren = searchCriteria.rooms.reduce((sum, room) => sum + (room.childAges?.length || 0), 0);
                      
                      return `Rooms: ${totalRooms} | Adults: ${totalAdults} | Children: ${totalChildren}`;
                    })()}
                  </span>
                </div>
              </div>

              {/* Price Information */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Price Details</h4>
                
                {/* Booking After Price */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Room Price:</span>
                  <span className="text-md font-semibold text-gray-900">
                    ₹{customerData.roomCardDetails?.room?.roomFareDetails?.bookingAfterPrice ? 
                      new Intl.NumberFormat('en-IN').format(customerData.roomCardDetails.room.roomFareDetails.bookingAfterPrice) : 'N/A'}
                  </span>
                </div>
                
                {/* Platform Fee */}
                {customerData.roomCardDetails?.room?.roomFareDetails?.platFormFee > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Platform Fee:</span>
                    <span className="text-md text-gray-900">
                      ₹{new Intl.NumberFormat('en-IN').format(customerData.roomCardDetails.room.roomFareDetails.platFormFee)}
                    </span>
                  </div>
                )}
                
                {/* GST on Platform Fee */}
                {customerData.roomCardDetails?.room?.roomFareDetails?.gstAmountOnPlatFormFee > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">
                      GST on Platform Fee 
                      {customerData.roomCardDetails?.room?.roomFareDetails?.gstPercentageOnPlatFormFee && 
                        ` (${customerData.roomCardDetails.room.roomFareDetails.gstPercentageOnPlatFormFee}%)`
                      }:
                    </span>
                    <span className="text-md text-gray-900">
                      ₹{new Intl.NumberFormat('en-IN').format(customerData.roomCardDetails.room.roomFareDetails.gstAmountOnPlatFormFee)}
                    </span>
                  </div>
                )}
                
                {/* Show discount if price changed */}
                {customerData.roomCardDetails?.room?.roomFareDetails?.differenceBetweenFare < 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center mb-1">
                      <svg className="w-4 h-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-semibold text-green-700">Discount Applied!</span>
                    </div>
                    <div className="text-xs text-green-600">
                      You save ₹{new Intl.NumberFormat('en-IN').format(Math.abs(customerData.roomCardDetails.room.roomFareDetails.differenceBetweenFare))}
                    </div>
                  </div>
                )}
                
                {/* Total Price */}
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Price:</span>
                    <span className="text-xl font-bold text-green-600">
                      ₹{(() => {
                        const roomFareDetails = customerData.roomCardDetails?.room?.roomFareDetails;
                        if (!roomFareDetails) return 'N/A';
                        
                        const roomPrice = roomFareDetails.bookingAfterPrice || 0;
                        const platformFee = roomFareDetails.platFormFee || 0;
                        const gstOnPlatformFee = roomFareDetails.gstAmountOnPlatFormFee || 0;
                        
                        const totalPrice = roomPrice + platformFee + gstOnPlatformFee;
                        
                        return new Intl.NumberFormat('en-IN').format(totalPrice);
                      })()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    (Inclusive of all charges)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Common Information</h3>
                
                <div className="space-y-6">
                  {/* Basic Info Section */}
                  <div>
                    <h4 className="text-md font-semibold text-blue-600 mb-3">Basic Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        placeholder="Mobile Number"
                        value={commonInfo.mobile}
                        onChange={(e) => handleCommonInfoChange('mobile', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={commonInfo.email}
                        onChange={(e) => handleCommonInfoChange('email', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Country"
                        value={commonInfo.country}
                        onChange={(e) => handleCommonInfoChange('country', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {(commonInfo.mobile || commonInfo.email || commonInfo.country) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (commonInfo.mobile) applyCommonInfo('mobile', commonInfo.mobile);
                          if (commonInfo.email) applyCommonInfo('email', commonInfo.email);
                          if (commonInfo.country) applyCommonInfo('country', commonInfo.country);
                        }}
                        className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                      >
                        Apply to All Adults
                      </button>
                    )}
                  </div>

                  {/* PAN Section - Show only if panMandatory is true */}
                  {customerData?.roomCardDetails?.room?.validations?.panMandatory && (
                    <div className="border-t pt-4">
                      <h4 className="text-md font-semibold text-green-600 mb-3">PAN Information</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="PAN Number"
                          value={commonInfo.pan}
                          onChange={(e) => handleCommonInfoChange('pan', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {commonInfo.pan && (
                        <button
                          type="button"
                          onClick={() => applyPanInfo(commonInfo.pan)}
                          className="mt-3 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm"
                        >
                          Apply PAN to All Adults ok
                        </button>
                      )}
                    </div>
                  )}

                  {/* Passport Section - Show only if passportMandatory is true */}
                  {customerData?.roomCardDetails?.room?.validations?.passportMandatory && (
                    <div className="border-t pt-4">
                      <h4 className="text-md font-semibold text-purple-600 mb-3">Passport Information</h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          type="text"
                          placeholder="Passport Number"
                          value={commonInfo.passportNo}
                          onChange={(e) => handleCommonInfoChange('passportNo', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="date"
                          placeholder="Passport Issue Date"
                          value={commonInfo.passportIssueDate}
                          onChange={(e) => handleCommonInfoChange('passportIssueDate', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="date"
                          placeholder="Passport Expiry Date"
                          value={commonInfo.passportExpDate}
                          onChange={(e) => handleCommonInfoChange('passportExpDate', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {(commonInfo.passportNo || commonInfo.passportIssueDate || commonInfo.passportExpDate) && (
                        <button
                          type="button"
                          onClick={() => {
                            if (commonInfo.passportNo) applyPassportInfo('passportNo', commonInfo.passportNo);
                            if (commonInfo.passportIssueDate) applyPassportInfo('passportIssueDate', commonInfo.passportIssueDate);
                            if (commonInfo.passportExpDate) applyPassportInfo('passportExpDate', commonInfo.passportExpDate);
                          }}
                          className="mt-3 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors text-sm"
                        >
                          Apply Passport Info to All
                        </button>
                      )}
                    </div>
                  )}

                  {/* GST Section - Show only if gstAllowed is true */}
                  {customerData?.roomCardDetails?.room?.validations?.gstAllowed && (
                    <div className="border-t pt-4">
                      <h4 className="text-md font-semibold text-orange-600 mb-3">GST Information</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="GST Number"
                          value={commonInfo.gstNumber}
                          onChange={(e) => handleCommonInfoChange('gstNumber', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Company Name"
                          value={commonInfo.gstCompanyName}
                          onChange={(e) => handleCommonInfoChange('gstCompanyName', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Company Address"
                          value={commonInfo.gstCompanyAddress}
                          onChange={(e) => handleCommonInfoChange('gstCompanyAddress', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="tel"
                          placeholder="Company Contact Number"
                          value={commonInfo.gstCompanyContactNumber}
                          onChange={(e) => handleCommonInfoChange('gstCompanyContactNumber', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="email"
                          placeholder="Company Email"
                          value={commonInfo.gstCompanyEmail}
                          onChange={(e) => handleCommonInfoChange('gstCompanyEmail', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2"
                        />
                      </div>

                      {(commonInfo.gstNumber || commonInfo.gstCompanyName) && (
                        <button
                          type="button"
                          onClick={() => {
                            if (commonInfo.gstNumber) applyGstInfo('gstNumber', commonInfo.gstNumber);
                            if (commonInfo.gstCompanyName) applyGstInfo('gstCompanyName', commonInfo.gstCompanyName);
                            if (commonInfo.gstCompanyAddress) applyGstInfo('gstCompanyAddress', commonInfo.gstCompanyAddress);
                            if (commonInfo.gstCompanyContactNumber) applyGstInfo('gstCompanyContactNumber', commonInfo.gstCompanyContactNumber);
                            if (commonInfo.gstCompanyEmail) applyGstInfo('gstCompanyEmail', commonInfo.gstCompanyEmail);
                          }}
                          className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm"
                        >
                          Apply GST Info to Booking
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Passenger Details Forms */}
              {passengerDetails.rooms.map((room, roomIndex) => (
                <div key={roomIndex} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                    </svg>
                    Room {room.roomNumber} Details
                  </h3>

                  {/* Adults Section */}
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-green-600 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Adults ({room.adults.length})
                    </h4>
                    
                    {room.adults.map((adult, adultIndex) => (
                      <div key={adultIndex} className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <h5 className="font-medium text-gray-900 mb-3">Adult {adultIndex + 1}</h5>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <select
                            value={adult.passengerTitle}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerTitle', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Mr.">Mr.</option>
                            <option value="Mrs.">Mrs.</option>
                            <option value="Ms.">Ms.</option>
                            <option value="Dr.">Dr.</option>
                          </select>
                          
                          <input
                            type="text"
                            placeholder="First Name *"
                            value={adult.passengerFirstName}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerFirstName', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                          
                          <input
                            type="text"
                            placeholder="Last Name *"
                            value={adult.passengerLastName}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerLastName', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <input
                            type="tel"
                            placeholder="Phone Number *"
                            value={adult.passengerPhoneNumber}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPhoneNumber', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                          
                          <input
                            type="email"
                            placeholder="Email *"
                            value={adult.passengerEmail}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerEmail', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                          
                          <input
                            type="text"
                            placeholder="Country *"
                            value={adult.passengerCountry}
                            onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerCountry', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        {customerData.roomCardDetails?.room?.validations?.panMandatory && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <input
                              type="text"
                              placeholder="PAN Card Number"
                              value={adult.passengerPANCardNumber}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPANCardNumber', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}

                        {customerData.roomCardDetails?.room?.validations?.passportMandatory && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <input
                              type="text"
                              placeholder="Passport Number"
                              value={adult.passengerPassportNo}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPassportNo', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            
                            <input
                              type="date"
                              placeholder="Passport Issue Date"
                              value={adult.passengerPassportIssueDate}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPassportIssueDate', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            
                            <input
                              type="date"
                              placeholder="Passport Expiry Date"
                              value={adult.passengerPassportExpDate}
                              onChange={(e) => handleInputChange(roomIndex, 'adults', adultIndex, 'passengerPassportExpDate', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Children Section */}
                  {room.children.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-md font-semibold text-yellow-600 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6V5a2 2 0 114 0v1H8zm2 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        Children ({room.children.length})
                      </h4>
                      
                      {room.children.map((child, childIndex) => (
                        <div key={childIndex} className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <h5 className="font-medium text-gray-900 mb-3">Child {childIndex + 1} (Age: {child.passengerAge})</h5>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <select
                              value={child.passengerTitle}
                              onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerTitle', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="Master">Master</option>
                              <option value="Miss">Miss</option>
                            </select>
                            
                            <input
                              type="text"
                              placeholder="First Name *"
                              value={child.passengerFirstName}
                              onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerFirstName', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                            
                            <input
                              type="text"
                              placeholder="Last Name *"
                              value={child.passengerLastName}
                              onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerLastName', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>

                          {customerData.roomCardDetails?.room?.validations?.passportMandatory && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <input
                                type="text"
                                placeholder="Passport Number"
                                value={child.passengerPassportNo}
                                onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerPassportNo', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              
                              <input
                                type="date"
                                placeholder="Passport Issue Date"
                                value={child.passengerPassportIssueDate}
                                onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerPassportIssueDate', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              
                              <input
                                type="date"
                                placeholder="Passport Expiry Date"
                                value={child.passengerPassportExpDate}
                                onChange={(e) => handleInputChange(roomIndex, 'children', childIndex, 'passengerPassportExpDate', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Special Requests - Hidden: Not required at this time */}
              {/* Arrival Information - Hidden: Not required at this time */}

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => window.close()}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={submitting || isExpired || !hasValidSession}
                  className={`px-6 py-2 rounded-md transition-colors ${
                    submitting || isExpired || !hasValidSession
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                      {showTimeoutWarning && (
                        <span className="text-yellow-200 text-xs">
                          ({Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))}s)
                        </span>
                      )}
                    </div>
                  ) : 'Confirm Passengers'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />

      {/* Error Modal with Close Tab */}
      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          window.close();
        }}
      />

      {/* Price Change Modal */}
      {showPriceChangeModal && priceChangeInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Great News! 🎉
              </h3>
              
              {/* Message */}
              <p className="text-gray-600 mb-4">
                You have a discount on this booking!
              </p>

              {/* Price Details */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Original Price:</span>
                  <span className="text-md text-gray-500 line-through">
                    ₹{new Intl.NumberFormat('en-IN').format(priceChangeInfo.originalPrice)}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-green-700">Discount Amount:</span>
                  <span className="text-lg font-bold text-green-600">
                    - ₹{new Intl.NumberFormat('en-IN').format(priceChangeInfo.discount)}
                  </span>
                </div>
                <div className="border-t border-green-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-md font-semibold text-gray-900">New Price:</span>
                    <span className="text-2xl font-bold text-green-600">
                      ₹{new Intl.NumberFormat('en-IN').format(priceChangeInfo.newPrice)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Confirmation Message */}
              <p className="text-gray-700 mb-6 font-medium">
                Do you want to continue with this updated price?
              </p>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPriceChangeModal(false);
                    window.close();
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  No, Cancel
                </button>
                <button
                  onClick={() => {
                    setShowPriceChangeModal(false);
                    toast.success('Price updated! You can now fill passenger details.');
                  }}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg"
                >
                  Yes, Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentCustomerDetailsPage;